/**
 * 📷 실사 사진 띠 (v2.224.0 · 2026-10-01 · 사용자 요청) — 주간 확인 카드 · 분기 실사 카드가 같이 쓴다.
 *
 * 사용자: 「그 항목마다 사진을 찍어서 첨부할 수 있어. 잘못찍는경우도 있으니까 찍은사진중 삭제할 수도 있어야 하고.
 *   항목별 사진 첨부 수량은 무제한. 그리고 이 기능은 매주 진행하는 재고실사에도 반영해줘.」
 *
 * · [📷 찍기] 는 폰 카메라를 바로 연다(capture) · [🖼 앨범] 은 사진첩. 여러 장 한 번에 고를 수 있다.
 * · 올리기 전에 폰에서 긴 변 1600px JPEG 로 줄인다(lib/stock-photo.ts). 한 장씩 차례로 올린다.
 * · 썸네일을 누르면 크게 본다(PdfPreviewModal 의 그림 보기). ✕ 는 한 번 묻고 지운다(NAS 에서도 지워진다).
 * · 바이트는 NAS 에 있다 — NAS 가 안 되는 날엔 **사진만** 실패하고 확인 기록은 그대로다. 실패 문구를 그대로 보인다.
 * ⚠`<input type="file">` 은 자동화로 못 누른다 — 검증은 DataTransfer 로 change 를 쏜다(회의록 첨부와 같은 방법).
 */

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/stock-audit-api';
import type { PhotoKey, StockPhoto } from '../lib/stock-audit-api';
import { preparePhoto } from '../lib/stock-photo';
import { openPdfPreview } from '../lib/pdf-preview';
import { dateTimeLabel } from '../lib/date-label';

interface Props {
  photoKey: PhotoKey;
  itemCode: string;
  /** 올리기·지우기 단추를 그릴지 (viewer·잠김이면 false) */
  canEdit: boolean;
  /** 잠긴 이유 — 있으면 단추 대신 이 글 */
  lockedReason?: string | null;
  /** 내 계정 — 남이 올린 사진의 ✕ 는 mgmt 이상만(서버와 같은 규칙 · 화면은 거들 뿐) */
  me?: { userId: string; role: string } | null;
  /** 썸네일만(이력 표 등) */
  compact?: boolean;
  onChanged?: () => void;
}

export function photoQueryKey(key: PhotoKey, itemCode: string): unknown[] {
  return key.scope === 'weekly'
    ? ['stock-photos', 'weekly', key.weekDate ?? 'cur', key.round ?? 'cur', itemCode]
    : ['stock-photos', 'audit', key.auditId, itemCode];
}

export function openPhoto(p: StockPhoto): void {
  openPdfPreview({
    url: api.photoFileUrl(p.id),
    downloadUrl: api.photoFileUrl(p.id),
    filename: p.filename,
    mime: p.mime,
    downloadLabel: '저장',
  });
}

export function StockPhotoStrip({ photoKey, itemCode, canEdit, lockedReason, me, compact, onChanged }: Props) {
  const qc = useQueryClient();
  const key = { ...photoKey, itemCode } as PhotoKey;
  const q = useQuery({
    queryKey: photoQueryKey(photoKey, itemCode),
    queryFn: () => api.listPhotos(key),
    staleTime: 30_000,
  });
  const photos = q.data?.photos ?? [];
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const cameraRef = useRef<HTMLInputElement>(null);
  const albumRef = useRef<HTMLInputElement>(null);

  const uploadKey = photoKey.scope === 'audit' ? { scope: 'audit' as const, auditId: photoKey.auditId } : { scope: 'weekly' as const };

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0 || !canEdit) return;
    const list = Array.from(files);
    setErr(null);
    let done = 0;
    try {
      for (const f of list) {
        setBusy(list.length > 1 ? `${done + 1}/${list.length} 올리는 중…` : '올리는 중…');
        const prepared = await preparePhoto(f);
        await api.uploadPhoto(uploadKey, itemCode, prepared.blob, f.name.replace(/\.[^.]+$/, '') + '.jpg');
        done += 1;
      }
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setErr(done > 0 ? `${done}장은 올라갔고 그다음에 실패했습니다 — ${m}` : m);
    } finally {
      setBusy(null);
      await qc.invalidateQueries({ queryKey: ['stock-photos'] });
      onChanged?.();
    }
  }

  async function remove(p: StockPhoto) {
    if (!canEdit || busy) return;
    if (!window.confirm(`이 사진을 지울까요?\n${dateTimeLabel(p.takenAt)} · ${p.takenByName}\n(NAS 에서도 지워집니다)`)) return;
    setErr(null);
    setBusy('지우는 중…');
    try {
      await api.deletePhoto(p.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      await qc.invalidateQueries({ queryKey: ['stock-photos'] });
      onChanged?.();
    }
  }

  const canRemove = (p: StockPhoto): boolean =>
    canEdit && !!me && (p.takenBy === me.userId || me.role === 'mgmt' || me.role === 'admin');

  return (
    <div className={compact ? '' : 'mt-3'}>
      {!compact && (
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-xs font-semibold text-slate-700">
            📷 사진 {photos.length > 0 && <span className="text-slate-400 font-normal">{photos.length}장</span>}
          </span>
          {q.data?.storeKind === 'local' && (
            <span className="text-[10px] text-amber-700" title="개발 PC 에서는 NAS 대신 임시 폴더에 둡니다">개발용 임시 저장</span>
          )}
        </div>
      )}

      {q.isError && (
        <div className="text-[11px] text-red-600 mb-1">사진 목록을 못 읽었습니다 — {(q.error as Error).message}</div>
      )}

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {photos.map((p) => (
            <div key={p.id} className="relative">
              <button
                type="button"
                onClick={() => openPhoto(p)}
                className="block w-[72px] h-[72px] rounded overflow-hidden border border-slate-200 bg-slate-100 active:opacity-80"
                title={`${dateTimeLabel(p.takenAt)} · ${p.takenByName}\n${p.nasPath}`}
              >
                {broken[p.id] ? (
                  <span className="flex items-center justify-center w-full h-full text-[10px] text-slate-500 text-center px-1 leading-tight">
                    NAS 에서<br />못 읽음
                  </span>
                ) : (
                  <img
                    src={api.photoFileUrl(p.id)}
                    alt={p.filename}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={() => setBroken((b) => ({ ...b, [p.id]: true }))}
                  />
                )}
              </button>
              {canRemove(p) && (
                <button
                  type="button"
                  onClick={() => void remove(p)}
                  disabled={!!busy}
                  aria-label="사진 지우기"
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-slate-800 text-white text-xs leading-none shadow disabled:opacity-50"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!compact && canEdit && (
        <div className="flex gap-2">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            data-photo-input="camera"
            onChange={(e) => { void onPick(e.target.files); e.target.value = ''; }}
          />
          <input
            ref={albumRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-photo-input="album"
            onChange={(e) => { void onPick(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={!!busy}
            className="flex-1 min-h-[40px] bg-white border border-slate-300 rounded text-sm font-semibold text-slate-700 active:bg-slate-100 disabled:opacity-50"
          >
            {busy ?? '📷 찍기'}
          </button>
          <button
            type="button"
            onClick={() => albumRef.current?.click()}
            disabled={!!busy}
            className="px-3 min-h-[40px] bg-white border border-slate-300 rounded text-sm text-slate-700 active:bg-slate-100 disabled:opacity-50"
          >
            🖼 앨범
          </button>
        </div>
      )}
      {!compact && !canEdit && lockedReason && photos.length === 0 && (
        <p className="text-[11px] text-slate-400">사진 없음 · {lockedReason}</p>
      )}
      {!compact && !canEdit && !lockedReason && photos.length === 0 && (
        <p className="text-[11px] text-slate-400">사진 없음</p>
      )}
      {err && <div className="mt-1.5 text-[11px] text-red-600 break-words">{err}</div>}
    </div>
  );
}
